import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateConfig } from '@/hooks/use-api';
import { configSchema, type ConfigFormData } from '@/lib/validations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Settings,
  Key,
  Send,
  Eye,
  EyeOff,
} from 'lucide-react';

type WizardStep = 'optimove' | 'provider' | 'senderId' | 'review';

const NewConfigWizard: React.FC = () => {
  const navigate = useNavigate();
  const createConfigMutation = useCreateConfig();
  const [currentStep, setCurrentStep] = useState<WizardStep>('optimove');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    optimoveSecretKey: false,
    providerApiKey: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    trigger,
    setError,
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema as any),
    defaultValues: {
      senderId: '',
      optimoveBaseUrl: '',
      optimoveApiToken: '',
      optimoveSecretKey: '',
      providerApiKey: '',
      providerSendUrl: 'https://api.int.onexglobal.io/api/jsmslist', // Default Onextel URL
    },
  });

  const watchedValues = watch();

  const steps = [
    { id: 'optimove', title: 'Optimove Credentials', icon: Settings },
    { id: 'provider', title: 'Provider Credentials', icon: Key },
    { id: 'senderId', title: 'Sender ID', icon: Send },
    { id: 'review', title: 'Review & Create', icon: Check },
  ];

  const getCurrentStepIndex = () =>
    steps.findIndex((step) => step.id === currentStep);
  const isLastStep = currentStep === 'review';
  const isFirstStep = currentStep === 'optimove';

  const validateCurrentStep = async (): Promise<boolean> => {
    let fieldsToValidate: (keyof ConfigFormData)[] = [];

    switch (currentStep) {
      case 'optimove':
        fieldsToValidate = [
          'optimoveBaseUrl',
          'optimoveApiToken',
          'optimoveSecretKey',
        ];
        break;
      case 'provider':
        fieldsToValidate = ['providerApiKey', 'providerSendUrl'];
        break;
      case 'senderId':
        fieldsToValidate = ['senderId'];
        break;
      case 'review':
        fieldsToValidate = [
          'senderId',
          'optimoveBaseUrl',
          'optimoveApiToken',
          'optimoveSecretKey',
          'providerApiKey',
          'providerSendUrl',
        ];
        break;
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) {
      toast.error('Please fix the errors before proceeding');
    }
    return isValid;
  };

  const handleNext = async () => {
    setIsTransitioning(true);
    const isValid = await validateCurrentStep();
    if (isValid && !isLastStep) {
      const nextIndex = getCurrentStepIndex() + 1;
      const nextStep = steps[nextIndex].id as WizardStep;
      setCurrentStep(nextStep);
    }
    // Small delay to prevent auto-submission during transition
    setTimeout(() => setIsTransitioning(false), 100);
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      const prevIndex = getCurrentStepIndex() - 1;
      setCurrentStep(steps[prevIndex].id as WizardStep);
    }
  };

  const togglePasswordVisibility = (
    field: 'optimoveSecretKey' | 'providerApiKey'
  ) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onSubmit = async (data: ConfigFormData) => {
    // Only allow submission if we're on the review step and not transitioning
    if (currentStep !== 'review' || isTransitioning) {
      return;
    }

    try {
      // Trim all credential fields to remove accidental spaces
      const trimmedData = {
        senderId: data.senderId.trim(),
        optimoveBaseUrl: data.optimoveBaseUrl.trim(),
        optimoveApiToken: data.optimoveApiToken.trim(),
        optimoveSecretKey: data.optimoveSecretKey.trim(),
        providerApiKey: data.providerApiKey.trim(),
        providerSendUrl: data.providerSendUrl.trim(),
      };

      const result = await createConfigMutation.mutateAsync(trimmedData);

      // Check if configuration was created successfully (handshake already verified in backend)
      if (result.config) {
        toast.success('Configuration created successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1800); // Changed from 1500 to 2000ms
      } else {
        // Handle handshake failure during creation
        if (result.handshake && !result.handshake.success) {
          const handshakeError = result.handshake;
          if (handshakeError.message) {
            toast.error(`Handshake failed: ${handshakeError.message}`);
          } else {
            toast.error(
              'Handshake failed. Please check your Optimove credentials and try again.'
            );
          }
        } else {
          toast.error('Failed to create configuration. Please try again.');
        }

        // Still navigate to dashboard after a delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      // Handle validation errors from server
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          setError(err.field as keyof ConfigFormData, {
            message: err.message,
          });
        });
        // For validation errors, also navigate after a longer delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        // Handle handshake failure or other errors
        const errorMessage =
          error.response?.data?.message || 'Failed to create configuration';
        toast.error(errorMessage);

        // Navigate to dashboard after 2 seconds for all errors
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'optimove':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="optimoveBaseUrl" className="text-sm">
                  Optimove Base URL
                </Label>
                <Input
                  id="optimoveBaseUrl"
                  type="text"
                  placeholder="https://your-optimove-instance.com"
                  className="h-9"
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  {...register('optimoveBaseUrl')}
                  aria-invalid={errors.optimoveBaseUrl ? 'true' : 'false'}
                />
                {errors.optimoveBaseUrl && (
                  <p className="text-xs text-destructive">
                    {errors.optimoveBaseUrl.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="optimoveApiToken" className="text-sm">
                  API Token
                </Label>
                <Input
                  id="optimoveApiToken"
                  type="password"
                  placeholder="Enter your Optimove API token"
                  className="h-9"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  {...register('optimoveApiToken')}
                  aria-invalid={errors.optimoveApiToken ? 'true' : 'false'}
                />
                {errors.optimoveApiToken && (
                  <p className="text-xs text-destructive">
                    {errors.optimoveApiToken.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="optimoveSecretKey" className="text-sm">
                  Secret Key
                </Label>
                <div className="relative">
                  <Input
                    id="optimoveSecretKey"
                    type={showPasswords.optimoveSecretKey ? 'text' : 'password'}
                    placeholder="Enter your Optimove secret key"
                    className="h-9 pr-8"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                    {...register('optimoveSecretKey')}
                    aria-invalid={errors.optimoveSecretKey ? 'true' : 'false'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() =>
                      togglePasswordVisibility('optimoveSecretKey')
                    }
                  >
                    {showPasswords.optimoveSecretKey ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                {errors.optimoveSecretKey && (
                  <p className="text-xs text-destructive">
                    {errors.optimoveSecretKey.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 'provider':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="providerApiKey" className="text-sm">
                  Provider API Key
                </Label>
                <div className="relative">
                  <Input
                    id="providerApiKey"
                    type={showPasswords.providerApiKey ? 'text' : 'password'}
                    placeholder="Enter your provider API key"
                    className="h-9 pr-8"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                    {...register('providerApiKey')}
                    aria-invalid={errors.providerApiKey ? 'true' : 'false'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => togglePasswordVisibility('providerApiKey')}
                  >
                    {showPasswords.providerApiKey ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                {errors.providerApiKey && (
                  <p className="text-xs text-destructive">
                    {errors.providerApiKey.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="providerSendUrl" className="text-sm">
                  Provider Send URL
                </Label>
                <Input
                  id="providerSendUrl"
                  type="url"
                  placeholder="https://api.provider.com/send"
                  value="https://api.int.onexglobal.io/api/jsmslist"
                  disabled
                  className="bg-muted h-9"
                  autoComplete="off"
                  {...register('providerSendUrl')}
                  aria-invalid={errors.providerSendUrl ? 'true' : 'false'}
                />
                <p className="text-xs text-muted-foreground">
                  Onextel provider URL is pre-configured and cannot be changed
                </p>
                {errors.providerSendUrl && (
                  <p className="text-xs text-destructive">
                    {errors.providerSendUrl.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 'senderId':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="senderId" className="text-sm">
                  Sender ID
                </Label>
                <Input
                  id="senderId"
                  type="text"
                  placeholder="my-sender-id"
                  className="h-9"
                  autoComplete="off"
                  {...register('senderId')}
                  aria-invalid={errors.senderId ? 'true' : 'false'}
                />
                {errors.senderId && (
                  <p className="text-xs text-destructive">
                    {errors.senderId.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Use letters, numbers, underscores, and hyphens only. Max 50
                  characters.
                </p>
              </div>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="border rounded-lg p-3 space-y-2">
                <h4 className="font-medium text-foreground text-sm">
                  Sender ID
                </h4>
                <p className="text-xs text-muted-foreground">
                  {watchedValues.senderId}
                </p>
              </div>

              <div className="border rounded-lg p-3 space-y-2">
                <h4 className="font-medium text-foreground text-sm">
                  Optimove Settings
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base URL:</span>
                    <span className="text-foreground font-mono">
                      {watchedValues.optimoveBaseUrl}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">API Token:</span>
                    <span className="text-foreground font-mono">
                      {'*'.repeat(watchedValues.optimoveApiToken.length)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Secret Key:</span>
                    <span className="text-foreground font-mono">
                      {'*'.repeat(watchedValues.optimoveSecretKey.length)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-2">
                <h4 className="font-medium text-foreground text-sm">
                  Provider Settings
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">API Key:</span>
                    <span className="text-foreground font-mono">
                      {'*'.repeat(watchedValues.providerApiKey.length)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Send URL:</span>
                    <span className="text-foreground font-mono">
                      {watchedValues.providerSendUrl}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-muted p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> When you click "Create Configuration",
                the system will first perform a handshake with Optimove to
                verify your credentials. Only if the handshake is successful
                will the configuration be created. If the handshake fails, no
                configuration will be saved.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-screen bg-gradient-background flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b header-border shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img
                  src="/onextel-logo-white.png"
                  alt="Onextel Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary">
                  Optimove - Onextel Setup
                </h1>
                <p className="text-xs text-muted">SMS Integration Dashboard</p>
              </div>
            </div>

            <div className="flex items-center space-x-4"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Page Header */}
          <div className="mb-4">
            {/* Back Button */}
            <Link
              to="/dashboard"
              className="inline-flex items-center text-muted hover:text-primary transition-colors group mb-3"
            >
              <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-0.5 transition-transform" />
              <span className="font-medium">Back</span>
            </Link>

            {/* Title */}
            <div>
              <h2 className="text-2xl font-bold text-primary mb-1">
                New Configuration
              </h2>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-4">
            <div className="flex">
              {steps.map((step, index) => {
                const isCompleted = getCurrentStepIndex() > index;
                const StepIcon = step.icon;

                return (
                  <div key={step.id} className="flex-1 text-center relative">
                    <div className="flex items-center justify-center">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 ${
                          isCompleted
                            ? 'bg-[#23203f] border-[#23203f] text-white'
                            : currentStep === step.id
                              ? 'border-[#23203f] bg-[#f0eef8] text-[#23203f]'
                              : 'border-gray-300 text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <StepIcon className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`h-0.5 absolute top-4 left-1/2 transform -translate-y-1/2 z-0 ${
                          getCurrentStepIndex() > index
                            ? 'bg-[#23203f]'
                            : 'bg-gray-200'
                        }`}
                        style={{ left: '75%', width: '50%' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`text-xs transition-all duration-300 text-center ${
                    currentStep === step.id
                      ? 'text-primary font-medium'
                      : 'text-gray-400'
                  }`}
                  style={{
                    width: `${100 / steps.length}%`,
                  }}
                >
                  {step.title}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="card-onextel p-4">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              autoComplete="off"
            >
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 'optimove'}
                  className="btn-outline"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {currentStep === 'review' ? (
                  <Button
                    type="submit"
                    disabled={isSubmitting || createConfigMutation.isPending}
                    className="btn-primary"
                  >
                    {isSubmitting || createConfigMutation.isPending ? (
                      <>
                        <span className="animate-pulse">
                          Creating & Verifying...
                        </span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Create Configuration
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewConfigWizard;
